import { KonvaEventObject } from "konva/lib/Node";
import { Comic, ElType } from "./comic.class";
import { PopUpInfo } from "../components/confirm-pop-up";
import { Dispatch, SetStateAction } from "react";
import { GetSet } from "../types";
import Utils from "./utils.class";
import { prenderNaJanela } from "./menu-na-janela";
import PosicaoNaPilha from "../components/posicao-na-pilha";
import { TouchableItem } from "./touchable-item";
import { Box } from "konva/lib/shapes/Transformer";
import { Shape } from "konva/lib/Shape";

export type RenderElProps = {
  comic: Comic;
  item: DraggableItem;
  isSelected: boolean;
  pageDims: { width: number; height: number };
  setPopUpInfo: Dispatch<SetStateAction<PopUpInfo | undefined>>;
  onClick: () => void;
  onDragMove: (
    e: KonvaEventObject<DragEvent | TouchEvent>,
    itemMenuRef?: HTMLDivElement | null
  ) => void;
  onDragEnd: (e: KonvaEventObject<DragEvent | TouchEvent>) => void;
  boundBoxFunc: (
    oldBox: Box,
    newBox: Box,
    itemMenuRef?: HTMLDivElement | null
  ) => Box;
  onTransformEnd: (e: KonvaEventObject<Event>) => void;
  // forceUpdate: () => void;
  EditingColor: GetSet<string | null>;
  scaleX?: number;
  scaleY?: number;
};

export type FirestoreDraggableItemObj = {
  type: ElType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  mirror: boolean;
  imgId: string;
};
export type DraggableItemObj = FirestoreDraggableItemObj & {
  id: string;
};

export abstract class DraggableItem extends TouchableItem {
  id: string;
  type: ElType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  mirror: boolean;
  imgId: string;
  image: HTMLImageElement;

  private isLoaded = false;
  protected abstract menuButtons: ((props: RenderElProps) => JSX.Element)[];
  protected imageRef: Shape | null = null;

  constructor(itemObj: DraggableItemObj, imgUrl: string) {
    super();

    this.id = itemObj.id || Utils.generateRandomId();
    this.type = itemObj.type;
    this.x = itemObj.x;
    this.y = itemObj.y;
    this.rotation = itemObj.rotation;
    this.mirror = itemObj.mirror;
    this.imgId = itemObj.imgId;
    this.width = itemObj.width;
    this.height = itemObj.height;
    this.mirror = itemObj.mirror;

    this.image = new Image();
    if (!imgUrl) {
      this.isLoaded = true;
    } else {
      this.image.onload = () => {
        this.isLoaded = true;
      };
      this.image.src = imgUrl;
    }
  }

  async waitImgLoad() {
    await (async () => {
      while (!this.isLoaded) await Utils.sleep(200);
    })();
  }

  getImage() {
    return this.mirror ? Utils.mirrorImage(this.image) : this.image;
  }

  toObj(): DraggableItemObj {
    return {
      id: this.id,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      rotation: this.rotation,
      mirror: this.mirror,
      type: this.type,
      imgId: this.imgId,
    };
  }

  toBox(): Box {
    return {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      rotation: Utils.degreesToRadians(this.rotation),
    };
  }
  getElCorners() {
    return Utils.getCorners(this.toBox());
  }
  /**
   * ONDE O MENU DO ITEM FICA — centrado acima dele, e DENTRO DA JANELA.
   *
   * A posição sai da medida do próprio menu (`clientWidth`/`clientHeight`), então ela acompanha
   * sozinha quando os botões mudam de tamanho. Foi o que permitiu levá-los de 20px para os 44 do
   * `T4.7` sem tocar em conta nenhuma.
   *
   * ── O QUE ELA NÃO FAZIA, E QUE FICOU CARO AO CRESCER ──────────────────────────────────────
   *
   * Nada impedia o menu de sair da tela. Com 20px de botão o estrago era pequeno; com 44 ele é
   * visível em dois casos que acontecem o tempo todo:
   *
   *   · item colado no ALTO da folha    -> o menu ia para cima do cabeçalho, ou para fora
   *   · item colado na BORDA lateral    -> metade do menu ficava fora da janela
   *
   * E um menu meio fora não é só feio: os botões que sobram fora não se clicam. Quem pusesse uma
   * personagem no canto perderia o apagar, o duplicar e as camadas — e concluiria, de novo, que
   * "não tem controle".
   *
   * Então: prende nas quatro bordas, e quando não há espaço EM CIMA o menu vai para BAIXO do
   * item. Nunca por cima dele — o menu tapando o que a criança está ajustando é o defeito que
   * este conserto existe para não criar.
   */
  getItemMenuPos(itemMenuRef?: HTMLDivElement | null) {
    const corners = this.getElCorners();
    const fallback = Utils.getClientRectTopMidPos(corners);
    if (!itemMenuRef) return fallback;

    // ── ONDE O ITEM ESTÁ NA TELA, E POR QUE ISSO NÃO É `this.x` ────────────────────────────
    //
    // `this.x/y` são coordenadas da FOLHA. A tela é outra coisa: a folha tem zoom, e está
    // desenhada dentro de uma caixa que rola. Antes, a conversão acontecia por acidente — o
    // `<Html>` do react-konva aplicava a transformação do palco no `<div>` do menu, e o
    // `position: fixed` do menu passava a valer dentro desse elemento transformado.
    //
    // Acidente que cobrava caro: a MESMA transformação escalava os botões. Medido a 91% de zoom,
    // botões de 44px chegavam com 40; a 24% (uma A4 em janela baixa) chegariam com 10. Alvo de
    // toque que encolhe com o zoom da folha não é alvo de toque.
    //
    // Com `transform={false}` no `<Html>`, o menu deixa de ser escalado — e a conversão passa a
    // ser responsabilidade daqui. `getClientRect()` do nó já devolve a caixa COM o zoom e a
    // rotação aplicados, em coordenadas do palco; somar a posição do container do palco na tela
    // dá a coordenada de tela de verdade.
    const no = this.imageRef;
    const palco = no?.getStage?.();
    const container = palco?.container?.();
    if (!no || !container) return fallback;

    const caixa = no.getClientRect();
    const r = container.getBoundingClientRect();
    const topoDoItem = { left: r.left + caixa.x + caixa.width / 2, top: r.top + caixa.y };
    const baseDoItem = r.top + caixa.y + caixa.height;

    // A conta mora em `menu-na-janela.ts` para poder ser testada: a propriedade que importa é
    // sobre quatro números e duas bordas, e não sobre o Konva. Sete casos em
    // `menu-na-janela.test.ts`, inclusive o do item colado no alto e o da janela mais estreita
    // que o próprio menu.
    return prenderNaJanela(
      topoDoItem,
      baseDoItem,
      { largura: itemMenuRef.clientWidth, altura: itemMenuRef.clientHeight },
      { largura: window.innerWidth, altura: window.innerHeight }
    );
  }

  mirrorEl() {
    this.mirror = !this.mirror;
    if (!!this.imageRef) {
      this.imageRef.setAttr("image", this.getImage());
    }
  }

  /**
   * T1.7 · O INDICADOR DE CAMADA ENTRA AQUI, E NAO NAS QUATRO CLASSES DE ITEM.
   *
   * Cada classe declara a propria lista de botoes, e as quatro tem os dois botoes de pilha. Se o
   * numero da camada fosse acrescentado em cada lista, a quinta classe nasceria sem ele — e este
   * produto ja pagou duas vezes pelo mesmo tipo de duplicacao (a lista de fontes do balao e o
   * rodape copiado). O menu se monta num lugar so, e e aqui.
   */
  getMenuBts(props: Omit<RenderElProps, "item">) {
    const daClasse = this.menuButtons.map((btn, i) => {
      const El = () => btn({ ...props, item: this });
      return <El key={i} />;
    });
    const Posicao = () => PosicaoNaPilha({ ...props, item: this });
    return [...daClasse, <Posicao key="posicao-na-pilha" />];
  }

  abstract render(props: RenderElProps): JSX.Element;
}
